import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FocusArea, toRichFocusArea, RichFocusArea } from '../lib/focusAreas';
import { IconChevronDown } from './Icons';

interface Props {
  focusAreas: FocusArea[];
  /**
   * Compact visual mode — tighter padding, smaller header, no default
   * card hover polish. Used when this block is demoted below the primary
   * insight to reduce top-of-page competition.
   */
  compact?: boolean;
}

/* ── Category pill colors ───────────────────────────────────────────── */
const CATEGORY_BG: Record<string, string> = {
  Sleep:          'bg-indigo-50 text-indigo-600',
  Recovery:       'bg-rose-50 text-rose-600',
  Nutrients:      'bg-amber-50 text-amber-700',
  Activity:       'bg-emerald-50 text-emerald-600',
  Stress:         'bg-purple-50 text-purple-600',
  'Mental Health': 'bg-red-50 text-red-600',
  Social:         'bg-sky-50 text-sky-600',
  Lifestyle:      'bg-slate-50 text-slate-600',
  Nutrition:      'bg-amber-50 text-amber-700',
  Health:         'bg-gray-50 text-gray-600',
};

function FocusCard({ area, index, expanded, onToggle }: {
  area: RichFocusArea;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const p = area.presentation;
  const catClass = CATEGORY_BG[p.category] ?? CATEGORY_BG.Health;

  return (
    <div className="group">
      {/* ── Layer 1: Action-first summary ── */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 text-left -mx-2 px-2 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
        aria-expanded={expanded}
      >
        {/* Rank badge */}
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-bold text-white"
          style={{ backgroundColor: area.color }}
        >
          {index + 1}
        </div>

        <div className="min-w-0 flex-1">
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${catClass}`}>
            {p.category}
          </span>
          <p className="text-sm font-semibold text-arete-navy leading-tight mt-1">
            {p.patientTitle}
          </p>
          <p className="text-xs text-arete-muted mt-0.5 leading-snug">
            {p.patientSubtitle}
          </p>
        </div>

        {/* Chevron */}
        <IconChevronDown
          size={16}
          className={`text-arete-muted flex-shrink-0 mt-1 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* ── Layer 2: Detail panel ── */}
      {expanded && (
        <div className="ml-8 mt-1 mb-2 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-3 text-xs">
          {/* Why this surfaced */}
          <div>
            <p className="font-semibold text-arete-navy mb-0.5">Why this surfaced</p>
            <p className="text-arete-muted leading-relaxed">{p.whySurfaced}</p>
          </div>

          {/* Easy next steps */}
          <div>
            <p className="font-semibold text-arete-navy mb-0.5">Helpful next steps</p>
            <ul className="text-arete-muted space-y-0.5 leading-relaxed">
              {p.nextSteps.map((step, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-arete-teal mt-px">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Clinical context (muted) */}
          {p.clinicalContext && (
            <div className="bg-gray-50 dark:bg-white/5 rounded-lg px-3 py-2">
              <p className="font-semibold text-arete-navy mb-0.5">Clinical context</p>
              <p className="text-arete-muted leading-relaxed">{p.clinicalContext}</p>
            </div>
          )}

          {/* Metric reference */}
          <div className="flex items-center justify-between pt-1">
            <p className="text-arete-muted">
              <span className="font-medium text-gray-500">Metric:</span> {area.title}
            </p>
            <Link
              to={area.link}
              className="text-arete-blue hover:underline font-medium flex items-center gap-1"
            >
              View details <span>→</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TopFocusAreas({ focusAreas, compact = false }: Props) {
  // In compact mode, collapse #1 by default so the block opens at its
  // smallest possible footprint. In normal mode, keep #1 expanded as the
  // hero focus.
  const [expandedIdx, setExpandedIdx] = useState<number | null>(compact ? null : 0);
  const [showAll, setShowAll] = useState(false);

  const richAreas = useMemo(() => focusAreas.map(toRichFocusArea), [focusAreas]);

  if (richAreas.length === 0) return null;

  const remaining = richAreas.length - 1;

  // Compact swaps the big .card padding (p-9 from index.css) for a
  // lighter p-5, keeping the glass background and rounding but halving
  // the vertical footprint.
  const cardClass = compact
    ? 'card !p-5 !rounded-2xl'
    : 'card';

  return (
    <div className={cardClass}>
      <h2 className="section-label mb-1">Your Top Priority</h2>

      {/* #1 focus area — always visible, expanded by default */}
      <FocusCard
        area={richAreas[0]}
        index={0}
        expanded={expandedIdx === 0}
        onToggle={() => setExpandedIdx(expandedIdx === 0 ? null : 0)}
      />

      {/* Remaining focus areas — behind toggle */}
      {remaining > 0 && (
        <>
          <button
            onClick={() => setShowAll(v => !v)}
            className="text-xs font-medium text-arete-blue hover:underline mt-2 mb-1 transition-colors"
          >
            {showAll ? 'Show less' : `See ${remaining} more focus area${remaining > 1 ? 's' : ''}`}
          </button>

          {showAll && (
            <div className="flex flex-col gap-1">
              {richAreas.slice(1).map((area, i) => (
                <FocusCard
                  key={area.concernType + (i + 1)}
                  area={area}
                  index={i + 1}
                  expanded={expandedIdx === i + 1}
                  onToggle={() => setExpandedIdx(expandedIdx === i + 1 ? null : i + 1)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
